import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Gauge, EnvelopeSimple, ShieldCheck, MapPin, Flag, Prohibit, MagnifyingGlass,
  Broadcast, ChartLineUp, Warning,
} from "@phosphor-icons/react";

const GEO_LABEL = { city: "Ville exacte", region: "Région", hq: "Siège / national", unknown: "Non localisé", "": "Non localisé" };
const GEO_COLOR = { city: "#00E5FF", region: "#4F46E5", hq: "#8A8A93", unknown: "#5A5A63" };

function Stat({ icon: Icon, label, value, sub, color = "#00E5FF", testid }) {
  return (
    <div className="surface rounded-lg p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-md flex items-center justify-center border" style={{ background: color + "14", borderColor: color + "40" }}>
          <Icon size={18} weight="duotone" style={{ color }} />
        </div>
        <span className="overline">{label}</span>
      </div>
      <div className="font-display text-3xl font-bold text-fg">{value}</div>
      {sub && <div className="text-xs text-muted mt-1">{sub}</div>}
    </div>
  );
}

function Bars({ title, data, colorFn }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div className="surface rounded-lg p-5">
      <div className="overline mb-4">{title}</div>
      {entries.length === 0 ? <div className="text-muted text-sm">Aucune donnée.</div> : (
        <div className="space-y-2.5">
          {entries.map(([k, v]) => (
            <div key={k}>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-fg/85">{GEO_LABEL[k] ?? k}</span>
                <span className="font-mono text-muted">{v}</span>
              </div>
              <div className="h-2 rounded-full bg-surface-2 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${(v / max) * 100}%`, background: colorFn ? colorFn(k) : "#00E5FF" }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminAiAnalytics() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/ai-analytics");
        setD(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="text-muted font-mono text-sm animate-pulse">Chargement…</div>;
  if (!d) return null;

  return (
    <div data-testid="admin-ai-analytics">
      <div className="overline mb-2">Pilotage</div>
      <h1 className="font-display text-3xl font-bold mb-1">Efficacité de l'IA</h1>
      <p className="text-muted text-sm mb-6 max-w-2xl">Mesurez la qualité des décideurs identifiés et l'acquisition de signaux, pour piloter votre GTM.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Stat testid="ai-stat-hit" icon={MagnifyingGlass} label="Taux de contact Prospeo" value={`${d.prospeo_hit_rate}%`} sub={`${d.contacts_with_email} / ${d.contacts_total} avec email`} />
        <Stat testid="ai-stat-verified" icon={ShieldCheck} label="Emails vérifiés" value={`${d.verified_email_rate}%`} sub={`${d.verified_contacts} décideurs vérifiés`} color="#22C55E" />
        <Stat testid="ai-stat-error" icon={Flag} label="Taux d'erreur signalé" value={`${d.error_rate}%`} sub={`${d.reports_total} signalement(s) ESN · ${d.reports_open} à traiter`} color="#EF4444" />
        <Stat testid="ai-stat-removed" icon={Prohibit} label="Décideurs blacklistés" value={d.curation_removals} sub="Retirés par l'admin (curation)" color="#F59E0B" />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat testid="ai-stat-contacts" icon={EnvelopeSimple} label="Décideurs en base" value={d.contacts_total} sub={`${d.prospeo_contacts} Prospeo · ${d.admin_added_contacts} ajout admin`} color="#4F46E5" />
        <Stat testid="ai-stat-signals" icon={Broadcast} label="Signaux collectés" value={d.signals_total} sub={`${d.multi_source_signals} multi-sources`} color="#00E5FF" />
        <Stat testid="ai-stat-searches" icon={ChartLineUp} label="Recherches IA" value={d.searches_total} color="#8B5CF6" />
        <Stat testid="ai-stat-feedback" icon={Warning} label="Retours ESN" value={`${d.feedback_relevant}/${d.feedback_wrong}`} sub="Pertinents / erreurs" color="#22C55E" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Bars title="Cohérence géographique des décideurs" data={d.geo_distribution} colorFn={(k) => GEO_COLOR[k] || "#8A8A93"} />
        <Bars title="Signaux par source (admin)" data={d.signals_by_source} />
      </div>
      <p className="text-xs text-muted mt-4 flex items-center gap-1"><MapPin size={12} /> « Ville exacte » = décideur trouvé dans la ville du besoin (idéal). « Siège » = repli quand aucun contact local n'existe.</p>
    </div>
  );
}
